import { cn } from '../../../../lib/utils';
import {
  CellColors,
  ColorType,
  DayProps,
  MapColorsToDays,
  SetBlockingColors,
  SetSelected
} from './colorUtils';

interface DatePickerDayProps extends DayProps {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (event: React.MouseEvent) => void;
  onMouseEnter?: (event: React.MouseEvent) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;
  tabIndex?: number;
}

function DatePickerDay({
  children,
  className,
  onClick,
  onMouseEnter,
  onMouseLeave,
  tabIndex,
  ...dayProps
}: DatePickerDayProps) {
  const dayWidth = 38;
  const dayHeight = 38;

  const initialColors = new CellColors();

  const colors: ColorType = {
    reservedColor: 'hsl(var(--secondary) / 0.5)',
    selectedColor: 'hsl(var(--primary) / 0.7)',
    defaultColor: 'hsl(var(--background))',
    textOutOfRangeColor: 'hsl(var(--muted-foreground) / 0.5)',
    textColor: 'inherit'
  };
  SetBlockingColors(dayProps, initialColors, colors);
  SetSelected(dayProps, initialColors, colors);

  const isBlockedCheckInAndCheckOut = dayProps.isBlockedCheckIn && dayProps.isBlockedCheckOut;

  const isSelected = dayProps.isSelectedStart || dayProps.isSelectedEnd || dayProps.isSelectedSpan;
  const isSelectedStartOrEnd = dayProps.isSelectedStart || dayProps.isSelectedEnd;

  const getTextColor = (): string => {
    if (dayProps.isBlockedOutOfRange && !isSelected) {
      return colors.textOutOfRangeColor;
    }
    if (isSelectedStartOrEnd) {
      return 'hsl(var(--primary-foreground))';
    }
    if (isSelected) {
      return 'hsl(var(--primary-foreground))';
    }
    return 'hsl(var(--foreground))';
  };

  const getStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      width: dayWidth,
      height: dayHeight,
      boxSizing: 'border-box',
      color: getTextColor()
    };

    // Selected start or end - solid primary with rounding
    if (isSelectedStartOrEnd && !dayProps.isBlockedOutOfRange) {
      return {
        ...baseStyles,
        background: 'hsl(var(--primary))',
        borderRadius: dayProps.isSelectedStart ? '8px 0 0 8px' : '0 8px 8px 0'
      };
    }

    // Day is both a check-out and check-in (transition between reservations)
    if (!dayProps.isBlockedOutOfRange && isBlockedCheckInAndCheckOut) {
      return {
        ...baseStyles,
        background: `linear-gradient(135deg, ${colors.reservedColor} 50%, ${colors.reservedColor} 50%)`
      };
    }

    if (!isBlockedCheckInAndCheckOut) {
      return {
        ...baseStyles,
        background: MapColorsToDays(initialColors, colors)
      };
    }

    return baseStyles;
  };

  const { isLockedCheckIn } = dayProps;
  const isHoverable =
    dayProps.isHovered &&
    !dayProps.isBlockedCalendar &&
    !dayProps.isBlockedOutOfRange &&
    !(dayProps.isBlockedCheckIn && dayProps.isBlockedCheckOut) &&
    !isLockedCheckIn;

  return (
    <td
      className={cn(
        'relative cursor-pointer',
        isLockedCheckIn && 'rounded border-2 border-warning',
        isHoverable && 'border border-primary',
        className
      )}
      style={getStyles()}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      tabIndex={tabIndex}
    >
      {children}
      {isLockedCheckIn && (
        <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-warning text-[10px] text-warning-foreground">
          🔒
        </span>
      )}
    </td>
  );
}

export default DatePickerDay;
